import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Countdown from 'react-countdown';
import classNames from 'classnames';

import { Sponsor } from './Sponsor';
import { SvgIcon } from 'common/react/components/SvgIcon';
import { MediaTag } from 'common/react/components/MediaTag';
import {
    getTranslation,
    lookupTranslation
} from 'common/react/utils/translations';
import { ImageElement } from 'common/react/components/ImageElement';
import { Media } from './Media';
import { getTimeString } from './util/getTimeString';
import { CountdownRenderer } from './CountdownRenderer';
import {
    MEDIATYPES,
    VERY_FREQUENT_REFETCH_INTERVAL,
    CLOSE_STREAM_END,
    SEMI_CLOSE_STREAM_END,
    FREQUENT_REFETCH_INTERVAL,
    LONG_REFETCH_INTERVAL
} from './constants';

export const TradeRadio = ({
    event,
    config,
    brightcoveProps,
    widgetClass = '',
    onStreamEnded = () => {
        return;
    }
}) => {
    let styleObj = {
        '--trade-accent': config.accentColour,
        '--trade-accent-text': config.accentTextColour,
        '--trade-accent-hover': config.accentHoverColour,
        '--trade-bg': config.backgroundColour
    };

    const videoPlayerEleId = `trade-radio_${brightcoveProps.anchorName}`;

    const { UPCOMING, LIVE } = PULSE.app.common.broadcast.status;

    const noCTAVisible =
        event.status === LIVE &&
        !(
            event.liveChannels?.AUDIO?.length &&
            event.liveChannels?.VIDEO?.length
        );

    // Active Channel stores what's currently loaded in the media player
    const [activeChannel, setActiveChannel] = useState(event.featureChannel);
    // Next channel queues up what we want to load into the media player
    const [nextChannel, setNextChannel] = useState(null);

    const [streamEnded, setStreamEnded] = useState(false);
    const [hasTriggeredRefetch, setHasTriggeredRefetch] = useState(false);
    const [lastEventId, setLastEventId] = useState(null);

    // We only want to record a thumbnail event once, this keeps track of that happening
    const [thumbnailEventRecorded, setThumbnailEventRecorded] = useState(false);

    // Keep track of current media type after select_cta event to stop repeat events being recorded
    const [currentMediaPlaying, setCurrentMediaPlaying] = useState(null);

    // First try and grab an image from the activeChannel, if that's null, swap to the nextChannel
    const mediaImageUrl =
        activeChannel?.customAttributes.find(
            (attr) => attr.name === 'thumbnailURL'
        )?.value ||
        nextChannel?.customAttributes.find(
            (attr) => attr.name === 'thumbnailURL'
        )?.value;

    const mediaType =
        activeChannel?.info?.channelTypes?.[0]?.mediaType || MEDIATYPES.VIDEO;

    /**
     * Callback function for when we want to start playing media from a channel
     *
     * @param {object} channelToPlay - The channel we want to play
     */
    const playMedia = (channelToPlay) => {
        const player = videojs.getPlayer(videoPlayerEleId);
        const currentMediaId = player?.mediainfo?.id;
        const idToPlay = channelToPlay.customAttributes.find(
            (attr) => attr.name === 'brightcove_videoid_web'
        )?.value;

        // If we've already loaded it, start playing
        if (currentMediaId === idToPlay) {
            player.play();
            // Otherwise queue it up to load
        } else {
            setNextChannel(channelToPlay);
        }
    };

    /**
     * Watch for when we have a nextChannel set to start swapping it out. We
     * have to do this to make sure we get 1 render to dispose of the video
     * player in brightcove, then remove it from the dom, then re-render it with
     * the new id's to re-init it.
     */
    useEffect(() => {
        // Next channel queued up but still have active channel
        if (nextChannel && activeChannel) {
            try {
                videojs.getPlayer(videoPlayerEleId).dispose();
            } catch (error) {
                console.error(
                    '[TradeRadio] There was an error disposing of the player',
                    error
                );
            }
            setActiveChannel(null);
            // Now the active channel is gone, swap in the nextChannel to active
        } else if (nextChannel && !activeChannel) {
            setActiveChannel({
                ...nextChannel
            });
            setNextChannel(null);
        }
    }, [activeChannel, nextChannel, videoPlayerEleId, onStreamEnded]);

    // Check if we're past the active channel's endDateTime
    useEffect(() => {
        if (!activeChannel?.endDateTime) {
            return;
        }

        const checkEndTime = () => {
            const currentTime = new Date().getTime();
            const endTime = new Date(activeChannel.endDateTime).getTime();
            const timeUntilEnd = endTime - currentTime;

            if (currentTime > endTime) {
                setStreamEnded(true);
                // Don't trigger refetch immediately - wait for countdown to complete
                // This prevents immediate refetch when there might be a next event
                return;
            }

            // Determine check interval based on time remaining
            let nextInterval;
            if (timeUntilEnd <= CLOSE_STREAM_END) {
                // Last 5 minutes: check every second
                nextInterval = VERY_FREQUENT_REFETCH_INTERVAL;
            } else if (timeUntilEnd <= SEMI_CLOSE_STREAM_END) {
                // Last 30 minutes: check every 30 seconds
                nextInterval = FREQUENT_REFETCH_INTERVAL;
            } else {
                // More than 30 minutes: check every 5 minutes
                nextInterval = LONG_REFETCH_INTERVAL;
            }

            // Set up next check
            const timeout = setTimeout(checkEndTime, nextInterval);
            return timeout;
        };

        // Start the checking process
        const initialTimeout = checkEndTime();

        return () => {
            if (initialTimeout) {
                clearTimeout(initialTimeout);
            }
        };
    }, [activeChannel?.endDateTime, event.id, onStreamEnded]);

    useEffect(() => {
        if (event && activeChannel && event.id !== lastEventId) {
            setStreamEnded(false);
            setHasTriggeredRefetch(false); // Reset refetch flag for new event
            setLastEventId(event.id);
        }
    }, [event, activeChannel, lastEventId]);

    // Handle immediate refetch when stream ends
    useEffect(() => {
        if (streamEnded && !hasTriggeredRefetch && onStreamEnded) {
            // Trigger refetch immediately when stream ends
            setHasTriggeredRefetch(true);
            onStreamEnded();
        }
    }, [streamEnded, hasTriggeredRefetch, onStreamEnded]);

    const handleCountdownComplete = () => {
        // When countdown completes for a new event, trigger refetch for next event
        // Only if we're not in a streamEnded state (meaning this is a genuine new event countdown)
        if (!streamEnded && onStreamEnded) {
            onStreamEnded();
        }
    };

    const displayedStreamData =
        event.status === UPCOMING
            ? event.channels.find(
                  (channel) => channel.info.id === activeChannel.info.id
              )
            : activeChannel;
    const shouldDisplayCountDown =
        event.status === UPCOMING
            ? new Date(displayedStreamData.startDateTime).getTime() >
              new Date().getTime()
            : true;

    return (
        <div className={widgetClass}>
            <article className="trade-radio" style={styleObj}>
                {noCTAVisible ? (
                    <button
                        className="trade-radio__card-button"
                        onClick={() => {
                            if (!thumbnailEventRecorded) {
                                PULSE.app.tracking.recordEvent('select_cta', {
                                    name: 'thumbnail',
                                    mediaType: mediaType.toLowerCase()
                                });
                                setThumbnailEventRecorded(true);
                            }
                            playMedia(displayedStreamData);
                        }}
                        aria-label={
                            mediaType === MEDIATYPES.AUDIO
                                ? getTranslation(
                                      'label.tradeRadio.listenButton'
                                  )
                                : getTranslation('label.tradeRadio.watchButton')
                        }
                    ></button>
                ) : null}

                {config?.promo?.promoItem?.onDemandUrl ? (
                    <Sponsor promo={config.promo} />
                ) : null}

                <div className="trade-radio__meta">
                    {event.status === LIVE ? (
                        <MediaTag
                            iconUrl={`/resources/${window.RESOURCES_VERSION}/i/elements/live-pulse-animated.svg`}
                            cssClass={
                                'trade-radio__live-indicator media-tag--live-highlight'
                            }
                            label={getTranslation('label.Live')}
                        />
                    ) : null}

                    {event.status === LIVE ? (
                        <h2 className="trade-radio__title">{event.name}</h2>
                    ) : (
                        <h2 className="trade-radio__title">
                            {lookupTranslation(
                                'label.tradeRadio.upcomingTitle',
                                {
                                    time: getTimeString(displayedStreamData)
                                }
                            )}
                        </h2>
                    )}

                    {config.subtitle ? (
                        <p className="trade-radio__desc">{config.subtitle}</p>
                    ) : null}
                </div>

                {config.linkTo && config.linkToLabel ? (
                    <div className="trade-radio__link-wrapper">
                        <a
                            className="trade-radio__link"
                            href={config.linkTo}
                            onClick={() => {
                                /* eslint-disable camelcase */
                                PULSE.app.tracking.recordEvent('select_link', {
                                    link_text: config.linkToLabel,
                                    link_url: config.linkTo,
                                    outbound: false,
                                    type: 'internal',
                                    component_name: `${
                                        event?.name ?? 'Trade Radio '
                                    }`
                                });
                                /* eslint-enable camelcase */
                            }}
                        >
                            {config.linkToLabel}{' '}
                            <SvgIcon className="icon" icon="arrow-right" />
                        </a>
                    </div>
                ) : null}

                <div
                    className={classNames('trade-radio__media', {
                        'trade-radio__media--full-height': noCTAVisible
                    })}
                    onClick={() => {
                        if (!thumbnailEventRecorded) {
                            PULSE.app.tracking.recordEvent('select_cta', {
                                name: 'thumbnail',
                                mediaType: mediaType.toLowerCase()
                            });
                            setThumbnailEventRecorded(true);
                        }
                    }}
                >
                    {event.status === LIVE && displayedStreamData ? (
                        <Media
                            featureChannel={displayedStreamData}
                            brightcoveProps={brightcoveProps}
                            videoPlayerEleId={videoPlayerEleId}
                            config={config}
                            title={event.name}
                            playsInline
                            onStreamEnded={() => setStreamEnded(true)}
                        />
                    ) : (
                        <ImageElement
                            url={mediaImageUrl}
                            objectFit={true}
                            width={16}
                            height={9}
                        />
                    )}
                </div>

                {event.status === UPCOMING ? (
                    shouldDisplayCountDown ? (
                        <div className="trade-radio__cta-wrapper">
                            <span className="trade-radio__join-cta">
                                {getTranslation(
                                    'label.tradeRadio.joinCtaPrefix'
                                )}{' '}
                                <strong>
                                    <Countdown
                                        date={displayedStreamData.startDateTime}
                                        renderer={CountdownRenderer}
                                        onComplete={handleCountdownComplete}
                                    />
                                </strong>
                            </span>
                        </div>
                    ) : null
                ) : !noCTAVisible ? (
                    <div className="trade-radio__cta-wrapper">
                        <button
                            className="trade-radio__cta-button button-new button-new--medium"
                            onClick={() => {
                                if (currentMediaPlaying !== MEDIATYPES.VIDEO) {
                                    PULSE.app.tracking.recordEvent(
                                        'select_cta',
                                        {
                                            name: 'watch live'
                                        }
                                    );
                                    setCurrentMediaPlaying(MEDIATYPES.VIDEO);
                                }
                                playMedia(event.liveChannels.VIDEO[0]);
                            }}
                        >
                            <SvgIcon
                                className="icon"
                                icon="video-playback-new"
                            />{' '}
                            {getTranslation('label.tradeRadio.watchButton')}
                        </button>
                        <button
                            className="trade-radio__cta-button button-new button-new--medium"
                            onClick={() => {
                                if (currentMediaPlaying !== MEDIATYPES.AUDIO) {
                                    PULSE.app.tracking.recordEvent(
                                        'select_cta',
                                        {
                                            name: 'listen live'
                                        }
                                    );
                                    setCurrentMediaPlaying(MEDIATYPES.AUDIO);
                                }
                                playMedia(event.liveChannels.AUDIO[0]);
                            }}
                        >
                            <SvgIcon className="icon" icon="listen" />{' '}
                            {getTranslation('label.tradeRadio.listenButton')}
                        </button>
                    </div>
                ) : null}
            </article>
        </div>
    );
};

TradeRadio.propTypes = {
    event: PropTypes.object.isRequired,
    config: PropTypes.object.isRequired,
    brightcoveProps: PropTypes.object.isRequired,
    widgetClass: PropTypes.string,
    onStreamEnded: PropTypes.func
};
